import classNames from 'classnames';
import { replace } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Sub, SubBold } from 'components/Sub';
import { ie10AndAbove } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Size from 'utils/Size';

const SizeButton = ({ label, available, active, options, className, ...otherProps }) => {
  const sizeStrings = Size.getSizeStrings(label);
  const isSquare = options ? options.isSquare : Size.isSizeButtonSquare(label);
  const isMultiline = options ? options.isMultiline : Size.isMultiWord(label);
  const hasParenthetical = Size.hasParenthetical(label);

  const classes = classNames('SizeButton', className, {
    'SizeButton--square': isSquare,
    'SizeButton--selected': active,
    'SizeButton--unavailable': !available,
    'SizeButton--multiline': isMultiline,
  });

  const ariaPressed = active ? 'true' : 'false';

  return (
    <button
      className={classes}
      {...otherProps}
      aria-label={`${I18n.localize('collectionPage.productCards.addSize')} ${label}`}
      aria-pressed={ariaPressed}
      type="button"
    >
      <span className="SizeButton-content">
        {sizeStrings && isSquare && (
          <>
            <span className="SizeButton-text SizeButton-text--locale-prefix">{sizeStrings[0]}</span>
            <span className="SizeButton-text SizeButton-text--size">{sizeStrings[1]}</span>
          </>
        )}
        {sizeStrings && !isSquare && (
          <>
            {hasParenthetical ? <SubBold>{sizeStrings[0]}</SubBold> : <Sub>{sizeStrings[0]}</Sub>}
            <Sub className="SizeButton__parenthetical">{sizeStrings[1]}</Sub>
          </>
        )}
        {!sizeStrings && <span className="SizeButton-text SizeButton-text--size">{label}</span>}
      </span>
      <style jsx>
        {`
          .SizeButton {
            appearance: none;
            width: 100%;
            position: relative;
            font-weight: ${Theme.get('font-weight-normal')};
            letter-spacing: ${Theme.get('letter-spacing-px')};
            font-size: ${Theme.get('font-size-small-px')};
            border: none;
            cursor: pointer;
            border: 1px solid ${Theme.get('colors-black')};
            border-radius: ${Theme.get('border-radius-px')};
            padding: 0;
            outline: none;
            color: ${Theme.get('colors-black')};
            background-color: transparent;
            transition: background ${Theme.get('transition-timing-ease-out-quad')}
              ${Theme.get('transition-duration') * 0.5}ms;
          }

          .SizeButton:disabled {
            color: ${Theme.get('colors-black-80')};
            border-color: ${Theme.get('colors-black-20')};
            cursor: initial;
          }

          .SizeButton:disabled.SizeButton:hover {
            color: ${Theme.get('colors-black-80')};
            background-color: transparent;
          }

          .SizeButton-text {
            z-index: 1;
          }

          .SizeButton-text--size {
            font-size: ${Theme.get('font-size-small-px')};
          }

          .SizeButton-text--locale-prefix {
            font-size: ${Theme.get('font-size-small-px')};
          }

          .SizeButton--square:after {
            content: '';
            display: block;
            padding-bottom: 100%;
          }

          .SizeButton-content {
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: ${Theme.get('spacing') * 2.25}px 0 ${Theme.get('spacing') * 2}px;
          }

          .SizeButton--multiline .SizeButton-content {
            padding: ${Theme.get('spacing-px')} 0;
          }

          .SizeButton--square .SizeButton-content {
            position: absolute;
            top: 0;
            left: 0;
            text-align: center;
            padding: 0;
          }

          .SizeButton:hover,
          .SizeButton:focus {
            color: ${Theme.get('colors-black')};
            background-color: ${Theme.get('colors-black-20')};
          }

          .SizeButton--selected,
          .SizeButton--selected:hover,
          .SizeButton--selected:focus,
          .SizeButton--unavailable.SizeButton--selected {
            color: ${Theme.get('colors-white')};
            background-color: ${Theme.get('colors-black')};
          }

          .SizeButton.focused:before {
            content: '';
            display: block;
            position: absolute;
            top: -4px;
            bottom: -4px;
            left: -4px;
            right: -4px;
            border-radius: ${Theme.get('border-radius-px')};
            border: solid 2px ${Theme.get('colors-black')};
          }

          @media ${ie10AndAbove} {
            .SizeButton--unavailable {
              color: ${Theme.get('colors-black-20')};
              border-color: ${Theme.get('colors-black-20')};
            }
          }

          /* Make sure to url encode any '#' as it is invalid for a URL */
          .SizeButton--unavailable .SizeButton-content::before {
            content: '';
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            margin: auto;
            background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><path d='M100 0 L0 100 ' stroke='${replace(
              Theme.get('colors-black'),
              '#',
              '%23'
            )}' stroke-width='1' /></svg>");
            background-repeat: no-repeat;
            background-position: center center;
            transition: none;
          }

          .SizeButton--unavailable.SizeButton--selected .SizeButton-content::before {
            background: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' preserveAspectRatio='none' viewBox='0 0 100 100'><rect width='100%' height='100%' fill='${replace(
              Theme.get('colors-black'),
              '#',
              '%23'
            )}'/><path d='M100 0 L0 100 ' stroke='${replace(
              Theme.get('colors-white'),
              '#',
              '%23'
            )}' stroke-width='1' /></svg>");
            background-repeat: no-repeat;
            background-position: center center;
            transition: none;
          }
        `}
      </style>
      <style jsx global>
        {`
          .SizeButton__parenthetical.Sub {
            font-size: ${Theme.get('font-size-small-px')};
          }

          .SizeButton:disabled .Sub,
          .SizeButton:disabled .Sub--bold {
            color: ${Theme.get('colors-black-80')};
          }

          .SizeButton.SizeButton--selected .Sub,
          .SizeButton.SizeButton--selected .Sub--bold {
            color: ${Theme.get('colors-white')};
          }

          .SizeButton .Sub {
            z-index: 1;
          }
        `}
      </style>
    </button>
  );
};

SizeButton.propTypes = {
  label: PropTypes.string.isRequired,
  available: PropTypes.bool,
  active: PropTypes.bool,
  options: PropTypes.shape({
    isSquare: PropTypes.bool,
    isMultiline: PropTypes.bool,
  }),
  className: PropTypes.string,
};

SizeButton.defaultProps = {
  available: true,
  active: false,
  className: '',
  options: null,
};

export default SizeButton;
